import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Package, Plus } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { formatPrice } from '../../../lib/utils';
import type { WarehouseCar } from '../../../types/car';

interface WarehouseListCardProps {
  warehouseCars: WarehouseCar[];
}

export function WarehouseListCard({
  warehouseCars,
}: WarehouseListCardProps) {
  const { t } = useLanguage();

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Package className="h-5 w-5" />
            {t('warehouse.inStock')}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {warehouseCars.length} {t('warehouse.carsAvailable')}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
            <Link to="/warehouse/add-car">
              <Plus className="h-4 w-4 mr-2" />
              {t('warehouse.addCar')}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {warehouseCars.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-500 mb-4">{t('warehouse.noCars')}</p>
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white">
              <Link to="/warehouse/add-car">
                <Plus className="h-4 w-4 mr-2" />
                {t('warehouse.addCar')}
              </Link>
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-600 font-medium text-sm">#</TableHead>
                  <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.vehicle')}</TableHead>
                  <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.year')}</TableHead>
                  <TableHead className="text-slate-600 font-medium text-sm">{t('sellCar.color')}</TableHead>
                  <TableHead className="text-slate-600 font-medium text-sm">{t('warehouse.purchasePrice')}</TableHead>
                  <TableHead className="text-slate-600 font-medium text-sm">{t('sellCarHistory.plateNumber')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {warehouseCars.map((car) => (
                  <TableRow key={car.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-sm text-slate-700">#{car.id}</TableCell>
                    <TableCell className="text-sm text-slate-700">
                      <span dir="ltr">{car.brand.name} {car.model.name}</span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700 tabular-nums">{car.year}</TableCell>
                    <TableCell className="text-sm text-slate-700">{car.color}</TableCell>
                    <TableCell className="text-sm text-slate-700 tabular-nums">{car.purchasePrice != null ? formatPrice(car.purchasePrice, car.purchaseCurrency ?? 'USD') : '-'}</TableCell>
                    <TableCell className="text-sm text-slate-700">{car.plateNumber || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
